import axios, { AxiosInstance, AxiosResponse, AxiosError } from 'axios';
import toast from '../../utils/toast';

// Types from api.ts
export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
}

export interface LoginRequest {
  emailOrUsername: string;
  password: string;
  rememberMe?: boolean;
}

export interface RegisterRequest {
  firstName: string;
  lastName: string;
  email: string;
  username: string;
  password: string;
  accountType: 'private' | 'company';
  companyName?: string;
  address?: {
    street: string;
    houseNumber: string;
    zipCode: string;
    city: string;
    country: string;
  };
  agreeToTerms: boolean;
  betaCode?: string;
}

// ============================================================================
// RENDITEFUCHS v3 - API Client Types
// ============================================================================
// Migration: INTEGER IDs -> UUID (string)
// ============================================================================

export interface User {
  id: string;  // UUID (v3 Migration)
  firstName: string;
  lastName: string;
  email: string;
  username?: string;
  role: 'user' | 'admin';
  subscription_tier?: 'FREE' | 'STARTER' | 'PRO' | 'UNLIMITED';
  accountType?: 'private' | 'company';
  companyName?: string;
  createdAt: string;
  isEmailVerified: boolean;
  isBetaTester?: boolean;
  betaJoinedAt?: string;
  avatarUrl?: string;
  address?: {
    street?: string;
    houseNumber?: string;
    zipCode?: string;
    city?: string;
    country?: string;
  };
}

export interface Subscription {
  id: string;  // UUID (v3 Migration)
  area: 'blog' | 'focus';
  tier: 'free' | 'standard' | 'premium';
  amount_cents: number;
  status: 'active' | 'canceled' | 'expired';
  created_at: string;
  expires_at?: string;
}

// API Configuration v2.0.1 - TRAILING SLASH FIX + LOCAL NETWORK SUPPORT
export const getApiBaseUrl = () => {
  // Wenn eine explizite URL gesetzt ist, verwende diese
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL;
  }

  const hostname = window.location.hostname;

  // Für lokale Entwicklung - direkt zum Backend Port 5555
  if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname.startsWith('192.168.')) {
    return `http://${hostname}:5555`;
  }

  // Production: Via Nginx (Same-Origin)
  return window.location.origin;
};

// Create axios instance without static baseURL
const axiosInstance: AxiosInstance = axios.create({
  timeout: 120000, // 120 seconds for stock data refresh (Alpha Vantage can be slow)
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// Request interceptor - Set baseURL dynamically and add auth token
axiosInstance.interceptors.request.use(
  (config) => {
    // Set baseURL dynamically for each request
    config.baseURL = getApiBaseUrl();
    // Check both localStorage (Remember Me) and sessionStorage (temporary)
    const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Demo-Rolle als Header senden (Admin-Rollenwechsel)
    const demoRole = localStorage.getItem('admin_selected_role');
    if (demoRole && demoRole !== 'admin') {
      config.headers['X-Demo-Role'] = demoRole;
    }

    // Add request timestamp for debugging
    if (process.env.NODE_ENV === 'development') {
      config.headers['X-Request-Time'] = new Date().toISOString();
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Throttling für wiederkehrende Toast-Meldungen (z.B. bei 429-Bursts):
// Verhindert, dass die UI mit hunderten identischen Fehler-Toasts überflutet
// wird, falls mehrere Requests parallel den gleichen Fehler zurückgeben.
const toastThrottle: Record<string, number> = {};
const TOAST_THROTTLE_MS = 5000;
const toastThrottled = (key: string, fn: () => void): void => {
  const now = Date.now();
  if (now - (toastThrottle[key] || 0) < TOAST_THROTTLE_MS) return;
  toastThrottle[key] = now;
  fn();
};

// Response interceptor - Handle errors globally
axiosInstance.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  async (error: AxiosError) => {
    const { response, message, config } = error;

    // Don't show toast for auth endpoints - let the forms handle errors
    const isAuthEndpoint = config?.url?.includes('/auth/');
    const isCheckoutEndpoint = config?.url?.includes('/subscriptions/payment-checkout') || config?.url?.includes('/subscriptions/paypal-checkout');
    // Hintergrund-Requests: Feature-Checks und Subscription-Status behandeln Fehler selbst
    const isBackgroundEndpoint = config?.url?.includes('/features/') || config?.url?.includes('/subscriptions/status');

    // 429-Auto-Retry: Einmaliger Retry mit Jitter-Backoff für idempotente GET-Requests.
    // Fängt Rest-Bursts auf, die durch Nginx-Limits noch durchschlagen.
    // Nur GET, nur 1 Retry, nicht für Auth-Endpoints (haben eigene Logik).
    if (
      response?.status === 429 &&
      config &&
      config.method?.toLowerCase() === 'get' &&
      !isAuthEndpoint &&
      !(config as any).__rateRetried
    ) {
      (config as any).__rateRetried = true;
      // Jitter 500-1500ms damit parallele 429er nicht synchron neu feuern
      const jitterMs = 500 + Math.floor(Math.random() * 1000);
      await new Promise((resolve) => setTimeout(resolve, jitterMs));
      return axiosInstance.request(config);
    }
    
    // Handle different error types
    if (response?.status === 401) {
      // For login attempts, don't redirect or show toast - let form handle it
      if (isAuthEndpoint) {
        return Promise.reject(error);
      }
      
      // Check if we're already on login page to prevent redirect loop
      const pathname = window.location.pathname;
      const isLoginPage = pathname === '/login' ||
                         pathname === '/register' ||
                         pathname === '/forgot-password';

      // Öffentliche Seiten (Blog, Landing, Kontakt, etc.) dürfen NICHT hart
      // zum Login redirecten, wenn ein Hintergrund-Request 401 liefert –
      // sonst verliert der Paywall-State auf /blog/:slug seine Seite.
      const isPublicPage = pathname === '/' ||
                          pathname.startsWith('/blog') ||
                          pathname.startsWith('/kontakt') ||
                          pathname.startsWith('/impressum') ||
                          pathname.startsWith('/agb') ||
                          pathname.startsWith('/datenschutz');

      if (!isLoginPage && !isPublicPage) {
        // Unauthorized - clear token and redirect to login
        localStorage.removeItem('auth_token');
        sessionStorage.removeItem('auth_token');
        window.location.href = '/login';
        toast.error('Sitzung abgelaufen. Bitte melden Sie sich erneut an.');
      } else {
        // Login-/Public-Page: Token stumm löschen, kein Redirect
        localStorage.removeItem('auth_token');
        sessionStorage.removeItem('auth_token');
      }
    } else if (response?.status === 402) {
      // Payment required - paywall hit
      const data = response.data as any;
      if (data?.paywall) {
        // This is expected for paywall responses, don't show error toast
        return Promise.reject(error);
      }
      toast.error('Upgrade erforderlich für diesen Inhalt');
    } else if (response?.status === 403) {
      // Skip toast for auth endpoints (email not verified errors)
      if (!isAuthEndpoint) {
        toast.error('Keine Berechtigung für diese Aktion');
      }
    } else if (response?.status === 404) {
      if (!isBackgroundEndpoint) {
        toast.error('Inhalt nicht gefunden');
      }
    } else if (response?.status === 429) {
      // Keine Toast-Meldung für Hintergrund-Requests oder Auth-Refresh-Calls
      // (z.B. /auth/user/profile, /subscriptions/status). Diese laufen oft
      // parallel und würden bei einem Rate-Limit die UI mit Toasts fluten.
      if (!isBackgroundEndpoint && !isAuthEndpoint) {
        toastThrottled('rate-limit', () =>
          toast.error('Zu viele Anfragen. Bitte versuchen Sie es sp\u00e4ter erneut.')
        );
      }
    } else if (response?.status === 500) {
      if (!isBackgroundEndpoint) {
        toastThrottled('server-error', () =>
          toast.error('Serverfehler. Bitte versuchen Sie es später erneut.')
        );
      }
    } else if (message === 'Network Error') {
      toastThrottled('network-error', () =>
        toast.error('Netzwerkfehler. Bitte prüfen Sie Ihre Internetverbindung.')
      );
    } else {
      // Generic error - skip toast for endpoints that handle their own errors
      if (!isAuthEndpoint && !isCheckoutEndpoint && !isBackgroundEndpoint) {
        const errorMessage = (response?.data as any)?.error?.message || 'Ein unbekannter Fehler ist aufgetreten';
        toast.error(errorMessage);
      }
    }
    
    return Promise.reject(error);
  }
);

// Helper functions
export const handleApiError = (error: any): string => {
  if (error.response?.data?.error?.message) {
    return error.response.data.error.message;
  }
  if (error.response?.data?.message) {
    return error.response.data.message;
  }
  if (error.message) {
    return error.message;
  }
  return 'Ein unbekannter Fehler ist aufgetreten';
};

export const isPaywallError = (error: any): boolean => {
  return error.response?.status === 402 && error.response?.data?.paywall === true;
};

export const getPaywallInfo = (error: any) => {
  if (isPaywallError(error)) {
    return error.response.data;
  }
  return null;
};

// NOTE: Legacy getApiUrl() removed - use getApiBaseUrl() instead (defined at top of file)
// getApiBaseUrl() detects environment automatically without hardcoded URLs

// Auth Service - compatible with api.ts interface
class AuthService {
  private axiosClient: AxiosInstance;

  constructor(axiosClient: AxiosInstance) {
    this.axiosClient = axiosClient;
  }

  private setToken(token: string | null, rememberMe: boolean = false) {
    if (token) {
      // Use localStorage if "Remember Me" is checked, otherwise sessionStorage
      if (rememberMe) {
        localStorage.setItem('auth_token', token);
        sessionStorage.removeItem('auth_token'); // Clear sessionStorage if it exists
      } else {
        sessionStorage.setItem('auth_token', token);
        localStorage.removeItem('auth_token'); // Clear localStorage if it exists
      }
    } else {
      localStorage.removeItem('auth_token');
      sessionStorage.removeItem('auth_token');
    }
  }

  async login(credentials: LoginRequest): Promise<ApiResponse<{ token: string; user: User }>> {
    try {
      // Client Hints auslesen (echte OS-Version statt eingefrorener UA-Version)
      let clientHints: Record<string, string> | undefined;
      try {
        const nav = navigator as any;
        if (nav.userAgentData?.getHighEntropyValues) {
          // Chromium-Browser: Client Hints API
          const data = await nav.userAgentData.getHighEntropyValues([
            'platformVersion', 'platform', 'fullVersionList', 'model',
          ]);
          clientHints = {};
          if (data.platformVersion) clientHints.platformVersion = data.platformVersion;
          if (data.platform) clientHints.platform = data.platform;
          if (data.model) clientHints.model = data.model;
          if (data.fullVersionList) {
            clientHints.fullVersionList = data.fullVersionList
              .map((b: { brand: string; version: string }) => `"${b.brand}";v="${b.version}"`)
              .join(', ');
          }
        } else {
          // Safari/Firefox: Keine Client Hints API verfügbar.
          // Basis-Infos senden für iPad-Erkennung (maxTouchPoints).
          clientHints = {};
          clientHints.maxTouchPoints = String(navigator.maxTouchPoints || 0);

          // iPhone erkennen (steht direkt im UA-String)
          if (/iPhone/.test(navigator.userAgent)) {
            clientHints.detectedPlatform = 'iOS';
          }
          // iPad erkennen: Meldet sich als Mac, hat aber Touch > 1
          else if (/Macintosh/.test(navigator.userAgent) && navigator.maxTouchPoints > 1) {
            clientHints.detectedPlatform = 'iPadOS';
          }
        }
      } catch { /* Client Hints nicht verfügbar */ }

      const loginData = {
        emailOrUsername: credentials.emailOrUsername,
        password: credentials.password,
        rememberMe: credentials.rememberMe,
        clientHints,
      };

      const response = await this.axiosClient.post('/auth/login', loginData);
      
      // Backend gibt {message: "...", user: {...}, token: "..."} zurück
      if (response.data?.token) {
        const backendUser = response.data.user;
        
        // Validate user data
        // Note: email can be empty for users who registered without email
        if (!backendUser || !backendUser.id || !backendUser.firstName || !backendUser.lastName) {
          throw new Error('Incomplete user data received from server');
        }
        
        const user: User = {
          id: backendUser.id,
          firstName: backendUser.firstName,
          lastName: backendUser.lastName,
          email: backendUser.email,
          username: backendUser.username || backendUser.email,
          role: backendUser.role || 'user',
          subscription_tier: backendUser.subscription_tier || 'FREE', // Add subscription tier
          accountType: backendUser.accountType || 'private',
          createdAt: backendUser.createdAt || new Date().toISOString(),
          isEmailVerified: backendUser.isEmailVerified || false
        };
        
        this.setToken(response.data.token, credentials.rememberMe);
        
        return {
          success: true,
          data: { token: response.data.token, user },
          message: response.data.message
        };
      }

      return {
        success: false,
        error: 'Login fehlgeschlagen'
      };
    } catch (error: any) {
      if (error.response?.data) {
        // Re-throw axios error for component handling
        throw error;
      }
      
      return {
        success: false,
        error: 'Login fehlgeschlagen. Bitte überprüfe deine Anmeldedaten.'
      };
    }
  }

  async register(userData: RegisterRequest): Promise<ApiResponse<{ token?: string; user?: User }>> {
    try {
      // Pass ALL registration data to backend, including company fields
      const registerData = {
        email: userData.email,
        password: userData.password,
        firstName: userData.firstName,
        lastName: userData.lastName,
        username: userData.username,
        accountType: userData.accountType,
        companyName: userData.companyName,
        agreeToTerms: userData.agreeToTerms,
        address: userData.address,
        betaCode: userData.betaCode
      };
      
      const response = await this.axiosClient.post('/auth/register', registerData);

      // Backend returns 201 with {message: "...", user: {...}} for successful registration
      if (response.status === 201 && response.data) {
        const backendUser = response.data?.user;
        
        // Handle successful registration
        if (backendUser && backendUser.id) {
          // Handle encrypted/decryption error data gracefully
          const user: User = {
            id: backendUser.id,
            firstName: backendUser.firstName === '[DECRYPTION_ERROR]' ? userData.firstName : backendUser.firstName,
            lastName: backendUser.lastName === '[DECRYPTION_ERROR]' ? userData.lastName : backendUser.lastName,
            email: backendUser.email === '[DECRYPTION_ERROR]' ? userData.email : backendUser.email,
            username: userData.username || userData.email, // Use provided username or email as fallback
            role: backendUser.role || 'user',
            accountType: userData.accountType || 'private',
            companyName: userData.companyName,
            createdAt: backendUser.createdAt || new Date().toISOString(),
            isEmailVerified: backendUser.emailVerified || false
          };
          
          // Show success message - don't use toast here as it may be handled in the component
          const successMessage = response.data.message || 'Registrierung erfolgreich! Bitte bestätige deine E-Mail-Adresse.';
          
          return {
            success: true,
            data: { user }, // No token for standard registration
            message: successMessage
          };
        }
      }

      return {
        success: false,
        error: 'Registrierung fehlgeschlagen'
      };
    } catch (error: any) {
      // Re-throw axios errors for component handling
      if (error.response?.data) {
        throw error;
      }
      
      return {
        success: false,
        error: 'Registrierung fehlgeschlagen. Bitte versuchen Sie es erneut.'
      };
    }
  }

  async forgotPassword(email: string): Promise<ApiResponse> {
    try {
      const response = await this.axiosClient.post('/auth/forgot-password', { email });

      return {
        success: true,
        data: response.data,
        message: response.data?.message || 'Passwort-Reset E-Mail wurde gesendet'
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Fehler beim Senden der Passwort-Reset E-Mail'
      };
    }
  }

  async resetPassword(token: string, password: string): Promise<ApiResponse> {
    try {
      const response = await this.axiosClient.post('/auth/reset-password', { token, password });

      return {
        success: true,
        data: response.data,
        message: response.data?.message || 'Passwort erfolgreich zurückgesetzt!'
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Fehler beim Zurücksetzen des Passworts'
      };
    }
  }

  async logout(): Promise<ApiResponse> {
    try {
      await this.axiosClient.post('/auth/logout');
    } catch (error) {
      // Logout can fail silently
    }
    
    // Always clear local auth data
    this.setToken(null); // This clears both localStorage and sessionStorage
    localStorage.removeItem('user_data');
    localStorage.removeItem('auth_user');
    
    return {
      success: true,
      message: 'Erfolgreich abgemeldet'
    };
  }

  async getUserProfile(): Promise<ApiResponse<any>> {
    try {
      const response = await axiosInstance.get('/auth/user/profile');
      return {
        success: true,
        data: response.data
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to fetch profile'
      };
    }
  }

  async getCurrentUser(): Promise<ApiResponse<{ user: User }>> {
    try {
      // Check both localStorage (Remember Me) and sessionStorage (temporary)
      const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
      if (!token) {
        return {
          success: false,
          error: 'No authentication token'
        };
      }
      
      const response = await this.axiosClient.get('/auth/me');
      
      return {
        success: true,
        data: { user: response.data.user }
      };
    } catch (error: any) {
      // Nur bei 401 (Unauthorized) das Token löschen - nicht bei Netzwerkfehlern oder Server-Fehlern
      if (error.response?.status === 401) {
        this.setToken(null);
        localStorage.removeItem('auth_token');
        localStorage.removeItem('user_data');
        localStorage.removeItem('auth_user');
      }

      return {
        success: false,
        error: error.response?.status === 401 ? 'Authentication failed' : 'Network or server error'
      };
    }
  }

  async verifyEmail(token: string): Promise<ApiResponse> {
    try {
      const response = await this.axiosClient.post('/auth/verify-email', { token });

      return {
        success: true,
        data: response.data,
        message: response.data?.message || 'E-Mail-Adresse wurde erfolgreich bestätigt!'
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Fehler bei der E-Mail-Bestätigung'
      };
    }
  }

  async resendVerification(email: string): Promise<ApiResponse<{ message: string; email: string }>> {
    try {
      const response = await this.axiosClient.post('/auth/resend-verification', { email });
      return {
        success: true,
        data: response.data
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Fehler beim Senden der Bestätigungs-E-Mail'
      };
    }
  }

  // Subscription endpoints
  async getSubscriptions(): Promise<ApiResponse<Subscription[]>> {
    try {
      const response = await this.axiosClient.get('/paywall/subscriptions');
      return {
        success: true,
        data: response.data
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Fehler beim Laden der Abonnements'
      };
    }
  }

  async checkAccess(area: 'blog' | 'focus', tier: 'free' | 'standard' | 'premium'): Promise<ApiResponse<{ hasAccess: boolean }>> {
    try {
      const response = await this.axiosClient.get(`/paywall/check-access?area=${area}&tier=${tier}`);
      return {
        success: true,
        data: response.data
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Fehler beim Überprüfen des Zugriffs'
      };
    }
  }

  async createSubscription(data: {
    area: 'blog' | 'focus';
    tier: 'standard' | 'premium';
  }): Promise<ApiResponse<Subscription>> {
    try {
      const response = await this.axiosClient.post('/paywall/subscriptions', data);
      return {
        success: true,
        data: response.data
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Fehler beim Erstellen des Abonnements'
      };
    }
  }

  // v3 Migration: subscriptionId is now UUID (string)
  async cancelSubscription(subscriptionId: string): Promise<ApiResponse> {
    try {
      const response = await this.axiosClient.delete(`/subscriptions/${subscriptionId}`);
      return {
        success: true,
        data: response.data
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Fehler beim Kündigen des Abonnements'
      };
    }
  }

  // Admin endpoints
  async getAdminStats(): Promise<ApiResponse<{
    totalUsers: number;
    activeUsers: number;
    newUsersThisWeek: number;
    adminUsers: number;
    betaTesters: number;
    regularUsers: number;
    systemHealth: number;
    uptime: string;
    totalLogins: number;
    avgDailyLogins: number;
    activeUsersDescription: string;
  }>> {
    try {
      const response = await this.axiosClient.get('/admin/stats');
      return {
        success: true,
        data: response.data.data
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Fehler beim Laden der Admin-Statistiken'
      };
    }
  }

  async getAdminUsers(): Promise<ApiResponse<any[]>> {
    try {
      const response = await this.axiosClient.get('/admin/users');
      return {
        success: true,
        data: response.data.data
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Fehler beim Laden der Benutzer'
      };
    }
  }

  async getBetaStats(): Promise<ApiResponse<{
    totalTesters: number;
    activeTesters: number;
    feedbackItems: number;
    avgResponseTime: number;
    newTestersThisWeek: number;
    activeTestersTrend: number;
    feedbackTrend: number;
    topContributors: Array<{
      id: string;  // UUID (v3 Migration)
      name: string;
      feedbackCount: number;
      feedbackTypes?: {
        feedback: number;
        feature: number;
        problem: number;
      };
    }>;
    feedbackByCategory: {
      feedback: number;
      problem: number;
      feature: number;
    };
    recentActivity: Array<{
      id: string;  // UUID (v3 Migration)
      type: string;
      user: string;
      message: string;
      time: string;
    }>;
  }>> {
    try {
      const response = await this.axiosClient.get('/admin/beta-stats');
      return {
        success: true,
        data: response.data.data
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Fehler beim Laden der Beta-Statistiken'
      };
    }
  }


  // --- Search Console / SEO ---

  async getGscStatus(): Promise<ApiResponse<{
    connected: boolean;
    siteUrl?: string;
    connectedAt?: string;
    isExpired?: boolean;
  }>> {
    try {
      const response = await this.axiosClient.get('/auth/search-console/status');
      return { success: true, data: response.data };
    } catch (error: any) {
      return { success: false, error: error.response?.data?.error || 'Fehler beim Pruefen des GSC-Status' };
    }
  }

  async connectGsc(): Promise<ApiResponse<{ authUrl: string }>> {
    try {
      const response = await this.axiosClient.get('/auth/search-console/connect');
      return { success: true, data: response.data };
    } catch (error: any) {
      return { success: false, error: error.response?.data?.error || 'Fehler beim Verbinden' };
    }
  }

  async disconnectGsc(): Promise<ApiResponse> {
    try {
      await this.axiosClient.delete('/auth/search-console/disconnect');
      return { success: true };
    } catch (error: any) {
      return { success: false, error: error.response?.data?.error || 'Fehler beim Trennen' };
    }
  }

  async getSeoSummary(days: number = 28): Promise<ApiResponse<{
    totalClicks: number;
    totalImpressions: number;
    averageCtr: number;
    averagePosition: number;
    clicksTrend: number;
    impressionsTrend: number;
  }>> {
    try {
      const response = await this.axiosClient.get(`/admin/seo/summary?days=${days}`);
      return { success: true, data: response.data.data };
    } catch (error: any) {
      return { success: false, error: error.response?.data?.error || 'Fehler beim Laden der SEO-Zusammenfassung' };
    }
  }

  async getSeoTopQueries(days: number = 28, limit: number = 25): Promise<ApiResponse<Array<{
    query: string;
    clicks: number;
    impressions: number;
    ctr: number;
    position: number;
  }>>> {
    try {
      const response = await this.axiosClient.get(`/admin/seo/top-queries?days=${days}&limit=${limit}`);
      return { success: true, data: response.data.data };
    } catch (error: any) {
      return { success: false, error: error.response?.data?.error || 'Fehler beim Laden der Top-Suchanfragen' };
    }
  }

  async getSeoPages(days: number = 28, limit: number = 25): Promise<ApiResponse<Array<{
    page: string;
    clicks: number;
    impressions: number;
    ctr: number;
    position: number;
  }>>> {
    try {
      const response = await this.axiosClient.get(`/admin/seo/pages?days=${days}&limit=${limit}`);
      return { success: true, data: response.data.data };
    } catch (error: any) {
      return { success: false, error: error.response?.data?.error || 'Fehler beim Laden der Seiten-Performance' };
    }
  }

  async getSeoDailyMetrics(days: number = 28): Promise<ApiResponse<Array<{
    date: string;
    clicks: number;
    impressions: number;
    ctr: number;
    position: number;
  }>>> {
    try {
      const response = await this.axiosClient.get(`/admin/seo/daily?days=${days}`);
      return { success: true, data: response.data.data };
    } catch (error: any) {
      return { success: false, error: error.response?.data?.error || 'Fehler beim Laden der taeglichen Metriken' };
    }
  }
}

// Create compatible instance with both AuthService methods and Axios methods
const authServiceInstance = new AuthService(axiosInstance);

// Create unified client with both auth methods and HTTP methods
const unifiedApiClient = {
  // AuthService methods (for components that use api.ts interface)
  login: authServiceInstance.login.bind(authServiceInstance),
  register: authServiceInstance.register.bind(authServiceInstance),
  logout: authServiceInstance.logout.bind(authServiceInstance),
  getCurrentUser: authServiceInstance.getCurrentUser.bind(authServiceInstance),
  getUserProfile: authServiceInstance.getUserProfile.bind(authServiceInstance),
  forgotPassword: authServiceInstance.forgotPassword.bind(authServiceInstance),
  resetPassword: authServiceInstance.resetPassword.bind(authServiceInstance),
  verifyEmail: authServiceInstance.verifyEmail.bind(authServiceInstance),
  resendVerification: authServiceInstance.resendVerification.bind(authServiceInstance),
  getSubscriptions: authServiceInstance.getSubscriptions.bind(authServiceInstance),
  checkAccess: authServiceInstance.checkAccess.bind(authServiceInstance),
  createSubscription: authServiceInstance.createSubscription.bind(authServiceInstance),
  cancelSubscription: authServiceInstance.cancelSubscription.bind(authServiceInstance),
  
  // Admin methods
  getAdminStats: authServiceInstance.getAdminStats.bind(authServiceInstance),
  getAdminUsers: authServiceInstance.getAdminUsers.bind(authServiceInstance),
  getBetaStats: authServiceInstance.getBetaStats.bind(authServiceInstance),

  // SEO / Search Console methods
  getGscStatus: authServiceInstance.getGscStatus.bind(authServiceInstance),
  connectGsc: authServiceInstance.connectGsc.bind(authServiceInstance),
  disconnectGsc: authServiceInstance.disconnectGsc.bind(authServiceInstance),
  getSeoSummary: authServiceInstance.getSeoSummary.bind(authServiceInstance),
  getSeoTopQueries: authServiceInstance.getSeoTopQueries.bind(authServiceInstance),
  getSeoPages: authServiceInstance.getSeoPages.bind(authServiceInstance),
  getSeoDailyMetrics: authServiceInstance.getSeoDailyMetrics.bind(authServiceInstance),
  
  // Direct Axios methods (for components that use generic HTTP calls)
  get: axiosInstance.get.bind(axiosInstance),
  post: axiosInstance.post.bind(axiosInstance),
  put: axiosInstance.put.bind(axiosInstance),
  delete: axiosInstance.delete.bind(axiosInstance),
  patch: axiosInstance.patch.bind(axiosInstance),
  request: axiosInstance.request.bind(axiosInstance),
};

export { unifiedApiClient as apiClient };
export default unifiedApiClient;